import { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  MessageCircle,
  Send,
  X,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";
import {
  useScheduledPosts,
  useCancelScheduledPost,
  type ScheduledPost,
  type ScheduledPostStatus,
} from "@/lib/hooks/useScheduledPosts";
import { RecurringDayList, RecurringMark, useRecurringDays } from "@/components/posts/RecurringDayList";

// ---------------------------------------------------------------------------
// Constantes / helpers de exibicao
// ---------------------------------------------------------------------------

const WEEKDAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const SAO_PAULO_TZ = "America/Sao_Paulo";

const STATUS_LABEL: Record<ScheduledPostStatus, string> = {
  pending: "Agendado",
  sending: "Enviando",
  sent: "Publicado",
  failed: "Falhou",
  canceled: "Cancelado",
};

const STATUS_BADGE_VARIANT: Record<ScheduledPostStatus, "success" | "warning" | "destructive" | "muted"> = {
  pending: "warning",
  sending: "warning",
  sent: "success",
  failed: "destructive",
  canceled: "muted",
};

const STATUS_DOT_CLASS: Record<ScheduledPostStatus, string> = {
  pending: "bg-amber-400",
  sending: "bg-amber-400",
  sent: "bg-primary",
  failed: "bg-destructive",
  canceled: "bg-muted-foreground/40",
};

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function formatMonthLabel(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}

function isToday(year: number, month: number, day: number): boolean {
  const today = new Date();
  return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
}

/** Chave de data (YYYY-MM-DD) do post no fuso America/Sao_Paulo, para agrupar
 *  no calendario pelo dia que o usuario efetivamente ve, nao pelo dia UTC. */
function toSaoPauloDateKey(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: SAO_PAULO_TZ }).format(new Date(iso));
}

function formatSaoPauloTime(iso: string): string {
  return new Intl.DateTimeFormat("pt-BR", { timeZone: SAO_PAULO_TZ, hour: "2-digit", minute: "2-digit" }).format(
    new Date(iso),
  );
}

function truncateText(text: string, max = 80): string {
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function CalendarioPage(): JSX.Element {
  const { data: posts = [], isLoading } = useScheduledPosts();
  const cancelPost = useCancelScheduledPost();

  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const monthLabel = formatMonthLabel(currentYear, currentMonth);

  const postsByDate = useMemo(() => {
    const map = new Map<string, ScheduledPost[]>();
    for (const post of posts) {
      const key = toSaoPauloDateKey(post.scheduledAt);
      const existing = map.get(key) ?? [];
      existing.push(post);
      map.set(key, existing);
    }
    return map;
  }, [posts]);

  const recurringByDate = useRecurringDays(currentYear, currentMonth);

  const selectedPosts = selectedDate ? (postsByDate.get(selectedDate) ?? []) : [];

  function goToPrevMonth(): void {
    if (currentMonth === 0) {
      setCurrentYear((y) => y - 1);
      setCurrentMonth(11);
    } else {
      setCurrentMonth((m) => m - 1);
    }
    setSelectedDate(null);
  }

  function goToNextMonth(): void {
    if (currentMonth === 11) {
      setCurrentYear((y) => y + 1);
      setCurrentMonth(0);
    } else {
      setCurrentMonth((m) => m + 1);
    }
    setSelectedDate(null);
  }

  const calendarDays = useMemo(() => {
    const days: Array<{ day: number; dateStr: string } | null> = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      days.push({ day: d, dateStr });
    }
    return days;
  }, [currentYear, currentMonth, firstDay, daysInMonth]);

  const upcomingPosts = useMemo(
    () => posts.filter((p) => p.status === "pending" || p.status === "sending"),
    [posts],
  );

  async function handleCancel(postId: string): Promise<void> {
    setFeedback(null);
    try {
      const canceled = await cancelPost.mutateAsync(postId);
      setFeedback(
        canceled
          ? "Post cancelado."
          : "Não foi possível cancelar: o post já saiu da fila (já foi enviado ou já está sendo processado).",
      );
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Erro ao cancelar o post.");
    }
  }

  return (
    <div className="max-w-5xl space-y-6">
      <PageHeader
        title="Calendário"
        subtitle="Acompanhe seus posts agendados e enviados, por destino e status."
      />

      {feedback && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-[hsl(var(--surface-raised)/0.4)] px-4 py-3 text-sm text-foreground">
          <span>{feedback}</span>
          <button type="button" onClick={() => setFeedback(null)} className="text-muted-foreground hover:text-foreground">
            <X size={14} />
          </button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Calendario mensal */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <button
                type="button"
                onClick={goToPrevMonth}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-md",
                  "text-muted-foreground transition-colors hover:bg-surface-raised hover:text-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
                aria-label="Mês anterior"
              >
                <ChevronLeft size={16} />
              </button>

              <h2 className="text-sm font-semibold capitalize text-foreground">{monthLabel}</h2>

              <button
                type="button"
                onClick={goToNextMonth}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-md",
                  "text-muted-foreground transition-colors hover:bg-surface-raised hover:text-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
                aria-label="Próximo mês"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <div className="grid grid-cols-7 border-b border-border">
              {WEEKDAY_LABELS.map((label) => (
                <div
                  key={label}
                  className="px-2 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                >
                  {label}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {calendarDays.map((cell, idx) => {
                if (!cell) {
                  return <div key={`empty-${idx}`} className="aspect-square border-b border-r border-border/50 bg-muted/10" />;
                }

                const postsOnDay = postsByDate.get(cell.dateStr) ?? [];
                const hasPosts = postsOnDay.length > 0;
                const isSelected = selectedDate === cell.dateStr;
                const isToday_ = isToday(currentYear, currentMonth, cell.day);

                return (
                  <button
                    key={cell.dateStr}
                    type="button"
                    onClick={() => setSelectedDate(cell.dateStr)}
                    className={cn(
                      "group relative flex aspect-square flex-col items-center justify-start p-1.5",
                      "border-b border-r border-border/50 transition-colors",
                      "hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                      isSelected && "bg-primary/10",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium",
                        isToday_ && "bg-primary text-primary-foreground font-semibold",
                        !isToday_ && "text-foreground",
                      )}
                    >
                      {cell.day}
                    </span>

                    <RecurringMark byDate={recurringByDate} date={cell.dateStr} />

                    {hasPosts && (
                      <div className="flex items-center gap-0.5">
                        {postsOnDay.slice(0, 3).map((post) => (
                          <span key={post.id} className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT_CLASS[post.status])} />
                        ))}
                        {postsOnDay.length > 3 && (
                          <span className="text-[10px] font-medium text-muted-foreground">+{postsOnDay.length - 3}</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Painel lateral: posts do dia selecionado */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="border-b border-border px-5 py-4">
              <h3 className="text-sm font-semibold text-foreground">
                {selectedDate
                  ? new Date(`${selectedDate}T12:00:00`).toLocaleDateString("pt-BR", { day: "2-digit", month: "long" })
                  : "Posts agendados"}
              </h3>
              {selectedDate && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {selectedPosts.length === 0
                    ? "Nenhum post neste dia"
                    : `${selectedPosts.length} ${selectedPosts.length === 1 ? "post" : "posts"}`}
                </p>
              )}
              {!selectedDate && (
                <p className="mt-0.5 text-xs text-muted-foreground">Selecione um dia no calendário para ver os posts.</p>
              )}
            </div>

            {selectedDate && selectedPosts.length > 0 && (
              <div className="divide-y divide-border">
                {selectedPosts.map((post) => (
                  <PostListItem key={post.id} post={post} onCancel={handleCancel} isCanceling={cancelPost.isPending} />
                ))}
              </div>
            )}

            {selectedDate && selectedPosts.length === 0 && (
              <EmptyState message="Nenhum post agendado para este dia." />
            )}

            <RecurringDayList byDate={recurringByDate} date={selectedDate} />

            {!selectedDate && <EmptyState message="Clique em um dia para ver os detalhes." />}
          </CardContent>
        </Card>
      </div>

      {/* Lista completa dos proximos posts */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-foreground">Próximos posts</h3>
        <div className="space-y-2">
          {isLoading && <p className="py-8 text-center text-sm text-muted-foreground">Carregando...</p>}
          {!isLoading && upcomingPosts.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">Nenhum post agendado no momento.</p>
          )}
          {upcomingPosts.map((post) => (
            <Card key={post.id} className="overflow-hidden">
              <CardContent className="flex items-center gap-4 px-5 py-3.5">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="hidden text-center sm:block">
                    <p className="text-lg font-semibold leading-tight text-foreground">
                      {new Date(post.scheduledAt).toLocaleDateString("pt-BR", { timeZone: SAO_PAULO_TZ, day: "2-digit" })}
                    </p>
                    <p className="text-[11px] font-medium uppercase text-muted-foreground">
                      {new Date(post.scheduledAt).toLocaleDateString("pt-BR", { timeZone: SAO_PAULO_TZ, month: "short" })}
                    </p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">{truncateText(post.bodyText)}</p>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock size={11} aria-hidden="true" />
                        {formatSaoPauloTime(post.scheduledAt)}
                      </span>
                      <DestinationSummary post={post} />
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={STATUS_BADGE_VARIANT[post.status]}>{STATUS_LABEL[post.status]}</Badge>
                  {post.status === "pending" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      disabled={cancelPost.isPending}
                      onClick={() => void handleCancel(post.id)}
                      aria-label="Cancelar post"
                    >
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subcomponentes
// ---------------------------------------------------------------------------

function EmptyState({ message }: { message: string }): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <CalendarIcon size={32} className="text-muted-foreground/30" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function DestinationSummary({ post }: { post: ScheduledPost }): JSX.Element {
  const telegramCount = post.deliveries.filter((d) => d.channel === "telegram").length;
  const whatsappCount = post.deliveries.filter((d) => d.channel === "whatsapp").length;

  return (
    <span className="flex items-center gap-2">
      {telegramCount > 0 && (
        <span className="flex items-center gap-1">
          <Send size={11} aria-hidden="true" />
          {telegramCount}
        </span>
      )}
      {whatsappCount > 0 && (
        <span className="flex items-center gap-1">
          <MessageCircle size={11} aria-hidden="true" />
          {whatsappCount}
        </span>
      )}
    </span>
  );
}

interface PostListItemProps {
  post: ScheduledPost;
  onCancel: (postId: string) => Promise<void>;
  isCanceling: boolean;
}

function PostListItem({ post, onCancel, isCanceling }: PostListItemProps): JSX.Element {
  const failedDeliveries = post.deliveries.filter((d) => d.status === "failed");

  return (
    <div className="space-y-2 px-5 py-4 transition-colors hover:bg-surface-raised/30">
      <p className="text-sm font-medium leading-snug text-foreground">{truncateText(post.bodyText, 120)}</p>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Clock size={11} aria-hidden="true" />
          {formatSaoPauloTime(post.scheduledAt)}
        </span>
        <DestinationSummary post={post} />
        <Badge variant={STATUS_BADGE_VARIANT[post.status]}>{STATUS_LABEL[post.status]}</Badge>
      </div>

      {failedDeliveries.length > 0 && (
        <ul className="space-y-1 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {failedDeliveries.map((delivery) => (
            <li key={delivery.id}>
              {delivery.label ?? delivery.targetId}: {delivery.lastError ?? "falha no envio"}
            </li>
          ))}
        </ul>
      )}

      {post.status === "pending" && (
        <Button type="button" variant="outline" size="sm" disabled={isCanceling} onClick={() => void onCancel(post.id)}>
          <X size={13} />
          Cancelar
        </Button>
      )}
    </div>
  );
}
